import { Subject } from 'rxjs';
import { startWith, takeUntil, debounceTime } from 'rxjs/operators';

import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Input,
  OnInit,
  Component,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  NgZone, // Required for running change detection in async operations outside Angular
} from '@angular/core';

import { SnMediaService } from '@lib/common/services';

import { SnAction } from '../../models';
import { SnActionConfig } from '../../types';

@Component({
  selector: 'sn-action-list',
  templateUrl: './action-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush, // Optimizes performance by only checking for changes when explicitly marked
  host: {
    class: 'sn-action-list',
  },
})
export class SnActionListComponent implements OnChanges, OnInit, OnDestroy {
  // Input property that accepts configuration for action items
  // Uses a setter to immediately process new configurations when they're received
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('items')
  set itemsConfig(value: SnActionConfig[]) {
    // Transform raw config objects into fully-fledged SnAction instances
    this._items = value.map(SnAction.fromConfig);
    // Initialize all items in expanded state - will be adjusted based on available space
    this._expandedItems = this._items.slice();
    this._collapsedItems = [];
  }

  @Input()
  public context: any;

  @Input()
  set alwaysCollapse(value: boolean) {
    this._alwaysCollapse = coerceBooleanProperty(value);
  }

  @Input()
  set strictCollapse(value: boolean) {
    this._strictCollapse = coerceBooleanProperty(value);
  }

  // Properties for internal state management
  private _items: SnAction[] = []; // Stores all action items
  private _expandedItems: SnAction[] = []; // Items currently shown in the main view
  private _collapsedItems: SnAction[] = []; // Items moved to the overflow menu
  private _alwaysCollapse: boolean; // Force all items to collapse regardless of space
  private _strictCollapse: boolean; // Strictly force all items to collapse (no exceptions)

  // Performance optimization properties
  private _containerWidth = 0; // Caches the container width to avoid repeated DOM reads
  private _resizeSubject = new Subject<void>(); // Used for debouncing resize events
  private readonly _destroy$ = new Subject<void>(); // Cleanup subject for unsubscribing
  private _observer: ResizeObserver; // Watches for container size changes

  constructor(
    private readonly _media: SnMediaService, // Handles responsive breakpoint changes
    private readonly _changeDetector: ChangeDetectorRef, // Manages manual change detection
    private readonly _elementRef: ElementRef, // Provides direct access to component's DOM element
    private readonly _ngZone: NgZone, // Handles execution context for async operations
  ) {}

  get expandedItems(): SnAction[] {
    return this._expandedItems;
  }

  get collapsedItems(): SnAction[] {
    return this._collapsedItems;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this._updateItems();
  }

  public ngOnInit(): void {
    this._mediaSubscription();
    this._setupResizeObserver();
  }

  public ngOnDestroy(): void {
    this._observer?.disconnect();
    this._destroy$.next();
    this._destroy$.complete();
  }

  public trackItem(index: number, item: SnAction): string {
    return `${index}-${item.constructor.name}`; // More reliable unique identifier
  }

  // Recursive function to find suitable container for width calculations
  private _findContainer(): HTMLElement | null {
    // Added container search logic to find first parent with defined width
    let element = this._elementRef.nativeElement.parentElement;
    while (element) {
      const style = window.getComputedStyle(element);
      const width = element.offsetWidth;
      // Look for elements with explicit width to ensure accurate measurements
      const hasDefinedWidth = style.width !== 'auto' && width > 0;

      if (hasDefinedWidth) {
        return element;
      }
      element = element.parentElement;
    }
    return null;
  }

  // Sets up the resize observation system
  private _setupResizeObserver(): void {
    const container = this._findContainer();
    if (!container) {
      console.warn('No suitable container found for action-list');
      return;
    }

    // Implement debounced resize handling to optimize performance
    this._resizeSubject
      .pipe(
        debounceTime(16), // Throttle to roughly 60fps for smooth performance
        takeUntil(this._destroy$), // Automatic cleanup on component destroy
      )
      .subscribe(() => {
        // Ensure Angular knows about async changes
        this._ngZone.run(() => {
          this._checkChildrenFit();
        });
      });

    // Initialize ResizeObserver with debounced handler
    this._observer = new ResizeObserver(() => {
      this._ngZone.run(() => {
        this._resizeSubject.next();
      });
    });
    this._observer.observe(container);
  }

  /**
   * Checks if the action items fit within their container and manages the expanded/collapsed states.
   *
   * This method performs the following steps:
   * 1. Finds the containing element with a defined width
   * 2. Filters visible items based on their hidden state
   * 3. Temporarily renders all items in expanded state
   * 4. Measures the total width of all items
   * 5. Determines if items should be collapsed based on container width
   * 6. Updates the expanded/collapsed states accordingly
   *
   * Error handling:
   * - If measurement fails, falls back to showing all non-hidden items in expanded state
   * - Items whose hidden state can't be determined are shown by default
   *
   * @private
   * @returns {void}
   */
  private _checkChildrenFit(): void {
    // Guard clauses for safety
    if (!this._elementRef?.nativeElement) return;

    const container = this._findContainer();
    if (!container) return;

    // Calculate width of all other children in the container
    let childWidth = 0;
    Array.from(container.children).forEach((child) => {
      if (child.nodeName !== 'SN-ACTION-LIST') {
        childWidth += child.clientWidth;
      }
    });

    // Update cached container width
    this._containerWidth = container.offsetWidth - childWidth;

    // Safely determine visible items, with error handling
    const visibleItems = (this._items || []).filter((item) => {
      try {
        return item && !item.hidden(this.context || {});
      } catch {
        return true;
      }
    });

    // Always start with expanded state for measurement
    this._expandedItems = visibleItems;
    this._collapsedItems = [];

    // Force immediate change detection
    this._changeDetector.detectChanges();

    // Measure in next frame after DOM updates
    requestAnimationFrame(() => {
      const expandedList = this._elementRef.nativeElement.querySelector('.expanded-items');
      if (!expandedList) return;

      // Get accurate total width including margins
      let totalWidth = 0;
      const children = Array.from(expandedList.children);

      children.forEach((child, index) => {
        const element = child as HTMLElement;
        if (element && visibleItems[index]) {
          const styles = window.getComputedStyle(element);

          const marginLeft = parseFloat(styles.marginLeft) || 0;
          const marginRight = parseFloat(styles.marginRight) || 0;
          const width = element.offsetWidth + marginLeft + marginRight;
          totalWidth += width;
        }
      });

      const OVERFLOW_BUTTON_WIDTH = 48;
      const BUFFER_MARGIN = 20; // Add buffer to prevent premature collapse

      // Consider both overflow button and buffer margin
      const availableWidth = this._containerWidth - (OVERFLOW_BUTTON_WIDTH + BUFFER_MARGIN);
      const shouldCollapse = this._alwaysCollapse || totalWidth > availableWidth;

      // Only collapse if we really need to
      if (
        this._strictCollapse ||
        (shouldCollapse && totalWidth > this._containerWidth - BUFFER_MARGIN)
      ) {
        this._expandedItems = [];
        this._collapsedItems = visibleItems;
      } else {
        this._expandedItems = visibleItems;
        this._collapsedItems = [];
      }

      // Ensure changes are detected
      this._ngZone.run(() => {
        this._changeDetector.detectChanges();
      });
    });
  }

  private _updateItems(): void {
    if (this._alwaysCollapse || this._strictCollapse) {
      this._expandedItems = [];
      this._collapsedItems = this._items.slice();
    } else {
      this._checkChildrenFit();
    }
  }

  private _mediaSubscription(): void {
    this._media.breakpointChange$
      .pipe(startWith(this._media.breakpoint), takeUntil(this._destroy$))
      .subscribe(() => {
        this._updateItems();
      });
  }
}
